import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { uploadFileThroughProxy } from 'shared-ui';
import { API_BASE_URL, BUSINESS_ID } from '../config/api';
import { Play, X } from 'lucide-react';

const API_BASE = API_BASE_URL;
const CHAT_BASE = `${API_BASE}/event-chat`;
const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

const TAB_DEFS = [
  { id: 'overview', ic: '🌊', label: 'Overview' },
  { id: 'organisers', ic: '👥', label: 'Organisers' },
  { id: 'addons', ic: '🍺', label: 'Add-ons' },
  { id: 'gear', ic: '🏕', label: 'Gear' },
  { id: 'food', ic: '🍉', label: 'Food' },
  { id: 'gallery', ic: '📸', label: 'Gallery' },
  { id: 'mystuff', ic: '🎒', label: 'My stuff' },
  { id: 'chat', ic: '💬', label: 'Chat' },
];

async function fetchStripePublishableKey() {
  try {
    const res = await fetch(`${API_BASE}/business/stripe-public${BUSINESS_ID ? `?businessId=${encodeURIComponent(BUSINESS_ID)}` : ''}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

function CardSetupForm({ onSaved, saving, setSaving, setError, customer, submitLabel, savingLabel }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        setError(error.message || 'Could not save your card');
        setSaving(false);
        return;
      }
      onSaved(setupIntent.payment_method);
    } catch (err) {
      setError(err.message || 'Could not save your card');
      setSaving(false);
    }
  };

  // Prefills Stripe's own "save for faster checkout" (Link) fields with what we already know
  // about the logged-in customer, so they aren't retyping their name/email/phone.
  const defaultValues = {
    billingDetails: {
      name: [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || undefined,
      email: customer?.email || undefined,
      phone: customer?.phone || undefined,
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ defaultValues }} />
      <button type="submit" disabled={!stripe || saving} style={{ marginTop: 16, padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
        {saving ? (savingLabel || 'Saving...') : (submitLabel || "Save card — you won't be charged yet")}
      </button>
    </form>
  );
}

// ---- shared inline style tokens for the Command Deck ----
const ACCENT = '#4f46e5';
const ACCENT_SOFT = '#eef1ff';
const deckTabsWrap = {
  display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch',
  // top matches the site header's rendered height (TrippyNavbar) — it's also position:sticky/top:0,
  // so without this offset the tab bar would stick to the same spot and disappear behind it.
  position: 'sticky', top: 83, zIndex: 20, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
  padding: '12px 2px 10px', margin: '0 0 18px', borderBottom: '1px solid #eef0f4',
};
const deckTabBtn = (on) => ({
  flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  background: on ? ACCENT : '#fff', border: `1px solid ${on ? ACCENT : '#e6e8ee'}`, borderRadius: 14,
  padding: '10px 14px', cursor: 'pointer', minWidth: 72,
  boxShadow: on ? '0 4px 12px rgba(79,70,229,0.28)' : '0 1px 2px rgba(16,24,40,0.04)',
  transition: 'all 0.15s ease',
});
const deckTabIcon = { fontSize: 17, lineHeight: 1 };
const deckTabLabel = (on) => ({ fontSize: 10.5, fontWeight: 600, color: on ? '#fff' : '#555' });
const deckTabBadge = { fontSize: 9.5, color: '#999' };
const deckWell = { background: '#fff', border: '1px solid #eef0f4', borderRadius: 18, padding: 20, minHeight: 160, boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.04)' };
const sectionHeading = { margin: '0 0 4px', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' };
const sectionSub = { fontSize: 13, color: '#8a8f98', margin: '0 0 14px' };
const cardStyle = { padding: 14, marginBottom: 10, background: '#fff', borderRadius: 14, border: '1px solid #eef0f4', fontSize: 14, boxShadow: '0 1px 2px rgba(16,24,40,0.04)' };

// ---- shared button tokens (used across My Stuff and elsewhere for a consistent modern feel) ----
const primaryBtnStyle = (disabled) => ({
  padding: '10px 18px', borderRadius: 10, cursor: disabled ? 'default' : 'pointer', border: 'none',
  background: disabled ? '#c7c9f5' : ACCENT, color: '#fff', fontSize: 14, fontWeight: 600,
  boxShadow: disabled ? 'none' : '0 2px 8px rgba(79,70,229,0.25)',
});
const secondaryBtnStyle = { padding: '9px 16px', borderRadius: 10, cursor: 'pointer', border: '1px solid #dcdfe6', background: '#fff', color: '#333', fontSize: 13, fontWeight: 600 };
const dangerBtnStyle = { padding: '9px 16px', borderRadius: 10, cursor: 'pointer', border: '1px solid #fca5a5', background: '#fff', color: '#dc3545', fontSize: 13, fontWeight: 600 };
const pillBtnStyle = (on) => ({
  padding: '7px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  border: `1px solid ${on ? ACCENT : '#dcdfe6'}`, background: on ? ACCENT : '#fff', color: on ? '#fff' : '#555',
});
const inputStyle = { width: '100%', padding: 10, borderRadius: 10, border: '1px solid #dcdfe6', marginBottom: 10, boxSizing: 'border-box', fontSize: 14 };

export default function EventSignup() {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || '';
  const inviteCodeFromUrl = searchParams.get('invite') || '';
  const { customer, isAuthenticated, getAuthHeaders, refreshCustomer } = useCustomerAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivateLocked, setIsPrivateLocked] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const [signupResult, setSignupResult] = useState(null); // { data, waitlisted }
  const [activeTab, setActiveTab] = useState('overview');
  const [myInviteLink, setMyInviteLink] = useState(null);
  const [inviteLinkBusy, setInviteLinkBusy] = useState(false);
  const [addOns, setAddOns] = useState([]);
  const [chatTopics, setChatTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null); // null = "All" (unified stream)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoadError, setChatLoadError] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatPhotoFile, setChatPhotoFile] = useState(null);
  const [chatItemName, setChatItemName] = useState('');
  const [showItemOffer, setShowItemOffer] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const [roles, setRoles] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [newNeedName, setNewNeedName] = useState('');
  const [newNeedIsOffer, setNewNeedIsOffer] = useState(true);
  const [addingNeed, setAddingNeed] = useState(false);
  const [offeringItemId, setOfferingItemId] = useState(null);
  const [offerPhotoFile, setOfferPhotoFile] = useState(null);
  const [offeringBusy, setOfferingBusy] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryUploadFile, setGalleryUploadFile] = useState(null);
  const [galleryCaption, setGalleryCaption] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [newFridayDishName, setNewFridayDishName] = useState('');
  const [newDishOccasion, setNewDishOccasion] = useState('friday');
  const [addingDish, setAddingDish] = useState(false);
  const [dishBusyId, setDishBusyId] = useState(null); // dishId currently being cooked/voted/etc
  const [attendees, setAttendees] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [groupBusy, setGroupBusy] = useState(false);
  const [newGroupForm, setNewGroupForm] = useState({ type: 'car', name: '', plateNumber: '', capacity: 2 });
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [invitePickerGroupId, setInvitePickerGroupId] = useState(null);
  const [messageThreads, setMessageThreads] = useState([]);
  const [activeThreadCustomerId, setActiveThreadCustomerId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [peopleExpanded, setPeopleExpanded] = useState(false);
  const [dietaryPreference, setDietaryPreference] = useState('');
  const [venueTiers, setVenueTiers] = useState([]);
  const [carCount, setCarCount] = useState(1);
  const [partySize, setPartySize] = useState(1);
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [proposedAddOnForm, setProposedAddOnForm] = useState({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
  const [proposingAddOn, setProposingAddOn] = useState(false);
  const [retryClientSecret, setRetryClientSecret] = useState(null);
  const [retryStripeCustomerId, setRetryStripeCustomerId] = useState(null);
  const [retrySaving, setRetrySaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [updatingDietary, setUpdatingDietary] = useState(false);
  const [expandedAddOnId, setExpandedAddOnId] = useState(null);

  const uploadFile = (file, meta) => uploadFileThroughProxy({ apiBaseUrl: API_BASE, tokenKey: 'customer_token', file, meta });

  const isDeferred = event?.paymentCaptureMode === 'deferred_threshold';
  const isImmediate = event?.paymentCaptureMode === 'immediate_on_signup';
  const isCarTiers = event?.venueSelectionMode === 'car_tiers';
  const hasCarPersonPricing = event?.pricePerCar != null || event?.pricePerPerson != null;
  const collectsCarPerson = isCarTiers || hasCarPersonPricing;
  const overThreshold = event?.thresholdReachedAt != null;
  const currentPerPersonRate = overThreshold && event?.pricePerPersonAtThreshold != null ? event.pricePerPersonAtThreshold : event?.pricePerPerson;
  // Once the threshold is hit, the car fee drops entirely — flat per-person rate.
  const carTierPricePreview = collectsCarPerson
    ? overThreshold && event?.pricePerPersonAtThreshold != null
      ? (currentPerPersonRate || 0) * partySize
      : (currentPerPersonRate || 0) * partySize + (event?.pricePerCar || 0) * carCount
    : null;

  const loadEvent = async (overrideCode) => {
    setLoading(true);
    setIsPrivateLocked(false);
    try {
      const codeToUse = overrideCode ?? codeFromUrl ?? manualCode;
      const qs = new URLSearchParams();
      if (codeToUse) qs.set('code', codeToUse);
      if (inviteCodeFromUrl) qs.set('invite', inviteCodeFromUrl);
      if (customer?.id) qs.set('customerId', customer.id);
      const res = await fetch(`${API_BASE}/events/public/${eventSlug}${qs.toString() ? `?${qs.toString()}` : ''}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (res.status === 403 && json.error === 'private_event') {
        setIsPrivateLocked(true);
        return;
      }
      if (!res.ok) throw new Error(json.error || 'Event not found');
      setEvent(json.event);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSlug]);

  // If the initial anonymous load hit the invite-only lock, retry once customer auth resolves —
  // an attendee who already has a ticket shouldn't need the code again just because auth loaded
  // after the first fetch (e.g. arriving here from a 'My Events' link with no code in the URL).
  useEffect(() => {
    if (isPrivateLocked && customer?.id) {
      loadEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivateLocked, customer?.id]);

  const handleUnlockWithCode = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    loadEvent(manualCode.trim());
  };

  useEffect(() => {
    fetchStripePublishableKey().then((key) => {
      if (key) setStripePromise(loadStripe(key));
    });
  }, []);

  const startCardSetup = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start signup');
      setClientSecret(json.data.clientSecret);
      setStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeSignup = async (stripePaymentMethodId) => {
    if (event.collectEmergencyContact && (!emergencyContactName.trim() || !emergencyContactPhone.trim())) {
      setError('Emergency contact name and phone are required for this event');
      setSavingCard(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          stripeCustomerId,
          stripePaymentMethodId,
          inviteLinkCode: inviteCodeFromUrl || undefined,
          ...(collectsCarPerson ? { carCount, partySize } : {}),
          ...(event.collectEmergencyContact ? { emergencyContactName: emergencyContactName.trim(), emergencyContactPhone: emergencyContactPhone.trim() } : {}),
          ...(dietaryPreference ? { dietaryPreference } : {}),
        }),
      });
      const json = await res.json();
      // A declined immediate charge (402) still creates the ticket (status payment_failed) — land
      // on the normal post-signup view so My Stuff's "update card and retry" flow is reachable,
      // instead of just showing an error and leaving them stuck on the signup form.
      if (!json.data) throw new Error(json.error || 'Signup failed');
      setSignupResult(json);
      if (!json.success && json.error) setError(json.error);
      loadEvent();
      loadMyInviteLink();
      loadAddOns();
    } catch (err) {
      setError(err.message);
      setSavingCard(false);
    }
  };

  const loadMyInviteLink = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/invite-links/mine?customerId=${customer.id}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.success) setMyInviteLink(json.data);
    } catch (err) {
      console.warn('Could not load invite link:', err.message);
    }
  };

  const handleGetInviteLink = async () => {
    if (!event || !customer) return;
    setInviteLinkBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/invite-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not create invite link');
      setMyInviteLink(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteLinkBusy(false);
    }
  };

  const loadAddOns = async () => {
    if (!event) return;
    try {
      const qs = customer ? `?customerId=${customer.id}` : '';
      const res = await fetch(`${API_BASE}/events/${event.id}/addons${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setAddOns(json.data || []);
    } catch (err) {
      console.warn('Could not load add-ons:', err.message);
    }
  };

  const loadVenueTiers = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/venue-tiers`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setVenueTiers(json.data || []);
    } catch (err) {
      console.warn('Could not load venue tiers:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && isCarTiers) loadVenueTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const loadMyTicket = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data) setSignupResult({ data: json.data, waitlisted: false });
    } catch (err) {
      console.warn('Could not load existing ticket:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && !signupResult) loadMyTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const startPaymentRetry = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start retry');
      setRetryClientSecret(json.data.clientSecret);
      setRetryStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeRetry = async (stripePaymentMethodId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/retry-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, stripeCustomerId: retryStripeCustomerId, stripePaymentMethodId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Payment failed');
      setSignupResult({ data: json.data, waitlisted: false });
      setRetryClientSecret(null);
      setRetryStripeCustomerId(null);
    } catch (err) {
      setError(err.message);
      setRetrySaving(false);
    }
  };

  const handleCancelSignup = async () => {
    if (!event || !customer) return;
    if (!window.confirm("Cancel your spot? You'll need to sign up again if you change your mind.")) return;
    setCancelling(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not cancel');
      setSignupResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleUpdateDietary = async (pref) => {
    if (!event || !customer) return;
    setUpdatingDietary(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, dietaryPreference: pref }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not update');
      setSignupResult((prev) => (prev ? { ...prev, data: { ...prev.data, dietaryPreference: json.data.dietaryPreference } } : prev));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingDietary(false);
    }
  };

  const handleProposeAddOn = async () => {
    if (!event || !customer) return;
    const { name, unitCost, unitYield, minSharePerPerson, unitYieldLabel } = proposedAddOnForm;
    if (!name || !unitCost || !minSharePerPerson) {
      setError('Name, unit cost, and minimum share per person are required');
      return;
    }
    setProposingAddOn(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          name,
          unitCost: Number(unitCost),
          unitYield: unitYield ? Number(unitYield) : undefined,
          unitYieldLabel: unitYieldLabel || undefined,
          minSharePerPerson: Number(minSharePerPerson),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not propose add-on');
      setProposedAddOnForm({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
      loadAddOns();
    } catch (err) {
      setError(err.message);
    } finally {
      setProposingAddOn(false);
    }
  };

  const loadChatMessages = async (topicId) => {
    if (!event) return;
    try {
      const qs = topicId ? `?topicId=${topicId}` : '';
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setChatMessages(json.data || []);
        setChatLoadError(null);
      } else {
        setChatLoadError(json.error || 'Could not load chat');
      }
    } catch (err) {
      setChatLoadError(err.message || 'Could not load chat');
    }
  };

  const loadChatTopics = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${CHAT_BASE}/${event.id}/topics`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setChatTopics(json.data || []);
    } catch (err) {
      console.warn('Could not load chat topics:', err.message);
    }
  };

  const loadRoles = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setRoles(json.data || []);
    } catch (err) {
      console.warn('Could not load roles:', err.message);
    }
  };

  const loadInventory = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setInventoryItems(json.data || []);
    } catch (err) {
      console.warn('Could not load inventory:', err.message);
    }
  };

  const loadDishes = async () => {
    if (!event) return;
    try {
      const qs = customer?.id ? `?customerId=${customer.id}` : '';
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setDishes(json.data || []);
    } catch (err) {
      console.warn('Could not load dishes:', err.message);
    }
  };

  const handleAddDish = async (name, occasion, onDone) => {
    if (!name.trim() || !event) return;
    setAddingDish(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: name.trim(), occasion, customerId: customer?.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not call that dish');
      onDone?.();
      loadDishes();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingDish(false);
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (!customer || !window.confirm('Remove this dish?')) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes/${dishId}?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not remove dish');
      loadDishes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClaimCook = async (dishId) => {
    if (!customer) return;
    setDishBusyId(dishId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes/${dishId}/cook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not claim this dish');
      loadDishes();
    } catch (err) {
      setError(err.message);
    } finally {
      setDishBusyId(null);
    }
  };

  const handleReleaseCook = async (dishId) => {
    if (!customer) return;
    setDishBusyId(dishId);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes/${dishId}/cook?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not release this dish');
      loadDishes();
    } catch (err) {
      setError(err.message);
    } finally {
      setDishBusyId(null);
    }
  };

  const handleVoteDish = async (dishId) => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/dishes/${dishId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not vote');
      loadDishes();
    } catch (err) {
      setError(err.message);
    }
  };

  const loadGallery = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setGalleryPhotos(json.data || []);
    } catch (err) {
      console.warn('Could not load gallery:', err.message);
    }
  };

  const loadAttendees = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/attendees`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setAttendees(json.data || []);
    } catch (err) {
      console.warn('Could not load attendees:', err.message);
    }
  };

  const loadMyGroups = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups/mine?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setMyGroups(json.data || []);
    } catch (err) {
      console.warn('Could not load groups:', err.message);
    }
  };

  const handleCreateGroup = async () => {
    if (!event || !customer || !newGroupForm.name.trim()) return;
    setGroupBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          type: newGroupForm.type,
          name: newGroupForm.name.trim(),
          plateNumber: newGroupForm.plateNumber.trim() || undefined,
          capacity: Number(newGroupForm.capacity) || 1,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not create');
      setNewGroupForm({ type: newGroupForm.type, name: '', plateNumber: '', capacity: 2 });
      loadMyGroups();
    } catch (err) {
      setError(err.message);
    } finally {
      setGroupBusy(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!event || !customer) return;
    if (!window.confirm('Delete this group? Anyone invited will lose their spot.')) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups/${groupId}?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not delete');
      loadMyGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInviteToGroup = async (groupId, inviteeCustomerId) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups/${groupId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, inviteeCustomerId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not invite');
      setInvitePickerGroupId(null);
      loadMyGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRespondToInvite = async (groupId, status) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups/${groupId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, status }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not respond');
      loadMyGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/groups/${groupId}/members/${customer.id}?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not leave');
      loadMyGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const loadMessageThreads = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/messages/threads?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setMessageThreads(json.data || []);
    } catch (err) {
      console.warn('Could not load message threads:', err.message);
    }
  };

  const loadThreadMessages = async (otherCustomerId) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/messages/${otherCustomerId}?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setThreadMessages(json.data || []);
    } catch (err) {
      console.warn('Could not load thread:', err.message);
    }
  };

  const handleOpenThread = (otherCustomerId) => {
    setActiveThreadCustomerId(otherCustomerId);
    loadThreadMessages(otherCustomerId);
  };

  const handleUploadAvatar = async (file) => {
    if (!file || !customer) return;
    setAvatarUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFile(file, { fileType: 'ATTACHMENT' });
      const res = await fetch(`${API_BASE}/customer-profile/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ avatarUrl: uploaded.url }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not save your photo');
      await refreshCustomer();
      loadAttendees();
      loadMyGroups();
      loadMessageThreads();
    } catch (err) {
      setError(err.message);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!event || !customer || !activeThreadCustomerId || !messageInput.trim()) return;
    setSendingMessage(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, toCustomerId: activeThreadCustomerId, content: messageInput.trim() }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not send');
      setMessageInput('');
      loadThreadMessages(activeThreadCustomerId);
      loadMessageThreads();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUploadGalleryPhoto = async () => {
    if (!event || !customer || !galleryUploadFile) return;
    setGalleryUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFile(galleryUploadFile, { fileType: 'ATTACHMENT' });
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, photoUrl: uploaded.url, caption: galleryCaption.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not post to the gallery');
      setGalleryUploadFile(null);
      setGalleryCaption('');
      loadGallery();
    } catch (err) {
      setError(err.message);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery/${photoId}?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not remove');
      loadGallery();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (signupResult) {
      loadMyInviteLink();
      loadAddOns();
      loadRoles();
      loadInventory();
      loadDishes();
      loadGallery();
      loadChatTopics();
      loadChatMessages(selectedTopicId);
      loadAttendees();
      loadMyGroups();
      loadMessageThreads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupResult?.data?.id]);

  useEffect(() => {
    if (signupResult && (activeTab === 'chat' || activeTab === 'mystuff')) {
      loadMyGroups();
      loadMessageThreads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (signupResult) loadChatMessages(selectedTopicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() && !chatPhotoFile) return;
    setSendingChat(true);
    setError(null);
    try {
      let photoUrl = null;
      if (chatPhotoFile) {
        const uploaded = await uploadFile(chatPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: chatInput.trim() || `Offering: ${chatItemName}`,
          topicId: selectedTopicId || undefined,
          photoUrl,
          inventoryItemName: showItemOffer ? chatItemName || undefined : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not send message');
      setChatInput('');
      setChatPhotoFile(null);
      setChatItemName('');
      setShowItemOffer(false);
      loadChatMessages(selectedTopicId);
      if (json.data?.linkedInventoryItemId) loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingChat(false);
    }
  };

  const handleClaimLeader = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/claim-leader`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not claim role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoinRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeaveRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/leave?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not leave role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNeed = async (name, category, onDone, postAsOffer = false) => {
    if (!name.trim()) return;
    setAddingNeed(true);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: name.trim(), category, customerId: customer?.id, postAsOffer }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not add need');
      onDone?.();
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingNeed(false);
    }
  };

  const handleOfferOnNeed = async (itemId) => {
    if (!customer) return;
    setOfferingBusy(true);
    setError(null);
    try {
      let photoUrl = null;
      if (offerPhotoFile) {
        const uploaded = await uploadFile(offerPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, photoUrl }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not offer item');
      setOfferingItemId(null);
      setOfferPhotoFile(null);
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setOfferingBusy(false);
    }
  };

  const handleWithdrawOffer = async (itemId) => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not withdraw offer');
      loadInventory();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddOnSignup = async (addOnId, quantity = 1) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons/${addOnId}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, quantity }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join add-on');
      loadAddOns();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleConfirmAddOnCharge = async (addOnId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons/${addOnId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not confirm');
      loadAddOns();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeclineAddOn = async (addOnId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons/${addOnId}/signup?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not decline');
      loadAddOns();
    } catch (err) {
      setError(err.message);
    }
  };

  const elementsOptions = useMemo(() => (clientSecret ? { clientSecret } : null), [clientSecret]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (isPrivateLocked) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <h2>This is an invite-only event</h2>
        <p style={{ color: '#666' }}>Ask the organizer for your invite link, or enter your code below.</p>
        <form onSubmit={handleUnlockWithCode} style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <input
            placeholder="Invite code"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
          />
          <button type="submit" style={{ padding: '10px 16px', cursor: 'pointer' }}>Unlock</button>
        </form>
      </div>
    );
  }
  if (error && !event) return <div style={{ padding: 40, textAlign: 'center', color: 'crimson' }}>{error}</div>;
  if (!event) return null;

  const progress = event.progress;
  const gearItems = inventoryItems.filter((it) => (it.category || 'gear') === 'gear' || it.category === 'other');
  const foodItems = inventoryItems.filter((it) => it.category === 'snacks');
  const unclaimedRolesCount = roles.filter((r) => !r.leader).length;
  const activeAddOns = addOns.filter((a) => a.status === 'active');
  const openGearCount = gearItems.filter((it) => it.status !== 'confirmed').length;
  const openFoodCount = foodItems.filter((it) => it.status !== 'confirmed').length;
  const myOfferedItems = inventoryItems.filter((it) => it.offers?.some((o) => o.customer?.id === customer?.id));
  const myAddOns = addOns.filter((a) => a.participants?.some((p) => p.customerId === customer?.id));
  const myCarGroups = myGroups.filter((g) => g.type === 'car');
  const mySleepGroups = myGroups.filter((g) => g.type !== 'car');
  const pendingGroupInvites = myGroups.filter((g) => g.members?.some((m) => m.customerId === customer?.id && m.status === 'invited')).length;
  const unreadMessagesCount = messageThreads.reduce((sum, t) => sum + (t.unreadCount || 0), 0);

  const TAB_BADGES = {
    organisers: unclaimedRolesCount > 0 ? `${unclaimedRolesCount} open` : null,
    addons: activeAddOns.length > 0 ? String(activeAddOns.length) : null,
    gear: openGearCount > 0 ? String(openGearCount) : null,
    food: openFoodCount > 0 ? String(openFoodCount) : null,
    gallery: galleryPhotos.length > 0 ? String(galleryPhotos.length) : null,
    mystuff: pendingGroupInvites > 0 ? String(pendingGroupInvites) : null,
    chat: unreadMessagesCount > 0 ? String(unreadMessagesCount) : chatMessages.length > 0 ? String(chatMessages.length) : null,
  };

  // -------------------- shared render helpers (used inside tab panels) --------------------

  const renderOverviewInfo = () => (
    <>
      {event.coverImage && (
        <img src={event.coverImage} alt={event.name} style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />
      )}
      {event.description && <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>}
      {event.videoUrl && (
        <div
          onClick={() => setVideoModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setVideoModalOpen(true); }}
          style={{
            position: 'relative',
            borderRadius: 12,
            overflow: 'hidden',
            marginBottom: 24,
            cursor: 'pointer',
            boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ position: 'relative', paddingTop: '56.25%', background: '#111' }}>
            <video src={event.videoUrl} muted playsInline preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div
              style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.15)',
              }}
            >
              <div style={{ background: 'rgba(0,0,0,0.55)', borderRadius: '999px', padding: 16, display: 'flex' }}>
                <Play size={28} color="white" fill="white" />
              </div>
            </div>
          </div>
        </div>
      )}
      {videoModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Event video"
          onClick={() => setVideoModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
            zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, position: 'relative' }}>
            <button
              type="button"
              onClick={() => setVideoModalOpen(false)}
              aria-label="Close video"
              style={{
                position: 'absolute', top: -44, right: 0, background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: '999px', color: 'white',
                padding: 8, cursor: 'pointer', display: 'flex',
              }}
            >
              <X size={20} />
            </button>
            <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <video
                src={event.videoUrl}
                controls
                autoPlay
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: '#000' }}
              />
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom: 20, padding: 14, background: '#f3f0ff', borderRadius: 8, border: '1px solid #d8cdfc' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#4c3d99' }}>
          🌱 This page is a work in progress — Zee's building it in the open, event by event, trying to find better ways for us to
          organise and connect. Rough edges are part of that. If something's confusing or breaks, say so — it helps shape what's next.
        </p>
      </div>
      {event.scheduleText && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 6 }}>Schedule</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.scheduleText}</p>
        </div>
      )}
      {event.safetyNote && (
        <div style={{ marginBottom: 20, padding: 14, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
          <h3 style={{ marginTop: 0, marginBottom: 6 }}>Safety</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.safetyNote}</p>
        </div>
      )}
      {isCarTiers && (
        <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
          <strong>Venue: we'll book the site that fits everyone</strong>
          {event.venueSelectionDate && (
            <p style={{ margin: '6px 0', fontSize: 14 }}>
              Decided on {new Date(event.venueSelectionDate).toLocaleDateString()} based on confirmed cars.
            </p>
          )}
          {venueTiers.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 14 }}>
              {venueTiers.map((tier) => (
                <li key={tier.id} style={{ fontWeight: event.selectedVenueTierId === tier.id ? 700 : 400 }}>
                  {tier.name} — up to {tier.maxCars} cars{tier.location ? ` (${tier.location})` : ''}
                  {event.selectedVenueTierId === tier.id ? ' ✅ selected' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  const renderStatusStrip = () => (
    progress && (
      <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
        <strong>{progress.confirmedCount} confirmed</strong>
        {event.maxCapacity ? ` (max ${event.maxCapacity})` : ''}
        {event.maxCapacity && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#666' }}>
            Once we hit {event.maxCapacity} people we'll close signups — anyone after that goes on the list for our next one.
          </p>
        )}
        {progress.waitlistCount > 0 && (
          <div style={{ margin: '8px 0 0' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
              {progress.waitlistCount} {progress.waitlistCount === 1 ? 'person has' : 'people have'} signed up for the next event.
            </p>
            {progress.waitlistNames?.length > 0 && (
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#666' }}>
                On the list: {progress.waitlistNames.join(', ')}
              </p>
            )}
          </div>
        )}
        {progress.minAttendeesToConfirm != null && progress.confirmedCount < progress.minAttendeesToConfirm && (
          <p style={{ margin: '8px 0 0', fontSize: 14 }}>
            We need {progress.minAttendeesToConfirm - progress.confirmedCount} more to lock the site in.
          </p>
        )}
        {hasCarPersonPricing && (
          <p style={{ margin: '8px 0 0', fontSize: 14 }}>
            {isImmediate ? (
              <>${currentPerPersonRate ?? 0}/person — covers the camping fee and food for the weekend.</>
            ) : overThreshold && event.pricePerPersonAtThreshold != null ? (
              <>
                Current rate: ${event.pricePerPersonAtThreshold}/person (no car fee) — locked in, we hit {event.chargeThresholdAttendees}.
                {' '}
                {event.chargeBatchTriggeredAt
                  ? 'You were notified, and cards have been charged.'
                  : event.thresholdReachedAt
                    ? `You've been notified — cards will be charged ${new Date(new Date(event.thresholdReachedAt).getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}.`
                    : ''}
              </>
            ) : (
              <>
                Current rate: ${event.pricePerCar ?? 0}/car + ${currentPerPersonRate ?? 0}/person
                {event.chargeThresholdAttendees && event.pricePerPersonAtThreshold != null && (
                  <> — drops the car fee and jumps to a flat ${event.pricePerPersonAtThreshold}/person for everyone once we hit {event.chargeThresholdAttendees}. You'll be notified the moment that happens, then charged 3 days later.</>
                )}
              </>
            )}
            {!isImmediate && event.registrationDeadline && (
              <> · nobody's charged until {new Date(event.registrationDeadline).toLocaleDateString()} or we hit {event.chargeThresholdAttendees}, whichever comes first</>
            )}
          </p>
        )}
      </div>
    )
  );

  const renderNeedOfferBoard = (items, category, emptyLabel, offerLabel = 'I have this', confirmLabel = 'Confirm offer', addPlaceholder, nameValue = newNeedName, onNameChange = setNewNeedName) => (
    <>
      {items.length === 0 ? (
        <p style={{ fontSize: 14, color: '#888' }}>{emptyLabel}</p>
      ) : (
        items.map((item) => {
          const myOffer = item.offers?.find((o) => o.customer?.id === customer?.id);
          return (
            <div key={item.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{item.name}</strong>
                {!myOffer ? (
                  <button onClick={() => setOfferingItemId(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    {offerLabel}
                  </button>
                ) : (
                  <button onClick={() => handleWithdrawOffer(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    Withdraw
                  </button>
                )}
              </div>
              {item.offers?.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {item.offers.map((o) => (
                    <div key={o.id} style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {o.photoUrl && <img src={o.photoUrl} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                      {o.customer?.firstName} {o.status === 'confirmed' ? '✅' : o.status === 'declined' ? '❌' : ''}
                    </div>
                  ))}
                </div>
              )}
              {offeringItemId === item.id && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="file" accept="image/*" onChange={(e) => setOfferPhotoFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
                  <button onClick={() => handleOfferOnNeed(item.id)} disabled={offeringBusy} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    {offeringBusy ? 'Saving...' : confirmLabel}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button onClick={() => setNewNeedIsOffer(true)} style={pillBtnStyle(newNeedIsOffer)}>
          I have this
        </button>
        <button onClick={() => setNewNeedIsOffer(false)} style={pillBtnStyle(!newNeedIsOffer)}>
          We need this
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder={addPlaceholder || (newNeedIsOffer ? 'What have you got? (e.g. a big pot)' : 'What do we need? (e.g. a big pot)')}
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <button
          onClick={() => handleAddNeed(nameValue, category, () => onNameChange(''), newNeedIsOffer)}
          disabled={addingNeed}
          style={{ padding: '10px 16px', cursor: 'pointer' }}
        >
          {addingNeed ? 'Adding...' : 'Add'}
        </button>
      </div>
    </>
  );

  // Food is a pure wishlist — the $60 ticket already covers food, so this isn't asking
  // attendees to bring/fulfill anything, just to say what they'd like to eat.
  const renderWishlistBoard = (items, category, emptyLabel, placeholder = 'What do you want to eat at camp? (e.g. alfajores)', nameValue = newNeedName, onNameChange = setNewNeedName) => (
    <>
      {items.length === 0 ? (
        <p style={{ fontSize: 14, color: '#888' }}>{emptyLabel}</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {items.map((item) => (
            <span key={item.id} style={{ padding: '6px 12px', background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 20, fontSize: 13 }}>
              {item.name}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          placeholder={placeholder}
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <button onClick={() => handleAddNeed(nameValue, category, () => onNameChange(''))} disabled={addingNeed} style={{ padding: '10px 16px', cursor: 'pointer' }}>
          {addingNeed ? 'Adding...' : 'Add'}
        </button>
      </div>
    </>
  );

  const AVATAR_COLORS = ['#e57373', '#ba68c8', '#7986cb', '#4db6ac', '#aed581', '#ffb74d', '#f06292'];
  const avatarColorFor = (id) => {
    if (!id) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  };
  const renderAvatar = (person, size = 32) => {
    const initials = `${person?.firstName?.[0] || ''}${person?.lastName?.[0] || ''}`.toUpperCase();
    return person?.avatarUrl ? (
      <img
        src={person.avatarUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    ) : (
      <div
        style={{
          width: size, height: size, borderRadius: '50%', flexShrink: 0,
          background: avatarColorFor(person?.id), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size * 0.4, fontWeight: 700,
        }}
      >
        {initials || '?'}
      </div>
    );
  };

  // A "Friday night dish" card: anyone can call/request one, one attendee can claim to cook it,
  // and anyone (not just the cook) can add and claim individual ingredients against it.
  const renderDishCard = (dish) => {
    const isCook = dish.cookId === customer?.id;
    const busy = dishBusyId === dish.id;
    const canDelete = customer && (dish.requestedById === customer.id || isCook);

    return (
      <div key={dish.id} style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <strong>{dish.name}</strong>
          {canDelete && (
            <button onClick={() => handleDeleteDish(dish.id)} style={{ ...dangerBtnStyle, padding: '4px 10px', fontSize: 12 }}>
              Remove
            </button>
          )}
        </div>
        {dish.requestedBy && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8a8f98' }}>
            Called by {dish.requestedBy.firstName} {dish.requestedBy.lastName}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0' }}>
          {dish.cook ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 4px', background: ACCENT_SOFT, borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
              {renderAvatar(dish.cook, 20)}
              🍳 {dish.cook.firstName} {dish.cook.lastName}{isCook ? ' (you)' : ''}
            </span>
          ) : (
            <button onClick={() => handleClaimCook(dish.id)} disabled={busy} style={secondaryBtnStyle}>
              {busy ? 'Claiming...' : "I'm cooking this"}
            </button>
          )}
          {isCook && (
            <button onClick={() => handleReleaseCook(dish.id)} disabled={busy} style={{ ...secondaryBtnStyle, padding: '6px 10px', fontSize: 12 }}>
              Not anymore
            </button>
          )}
          <button onClick={() => handleVoteDish(dish.id)} style={pillBtnStyle(dish.votedByMe)}>
            👍 {dish.voteCount || 0}
          </button>
        </div>
      </div>
    );
  };

  const GROUP_TYPE_LABELS = { car: '🚗 Car', tent: '⛺ Tent', vehicle_sleep: '🚐 Sleeping in car/van' };

  // A car or sleeping space is invite-only — only the owner and accepted members ever see who's
  // in it. Not an open board: the owner picks specific people from the attendee directory.
  const renderGroupCard = (group) => {
    const isOwner = group.ownerCustomerId === customer?.id;
    const myMembership = group.members?.find((m) => m.customerId === customer?.id);
    const acceptedMembers = group.members?.filter((m) => m.status === 'accepted') || [];
    const pendingMembers = group.members?.filter((m) => m.status === 'invited') || [];
    const spotsLeft = group.capacity - acceptedMembers.length;
    const alreadyInvitedIds = new Set(group.members?.map((m) => m.customerId));

    return (
      <div key={group.id} style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{GROUP_TYPE_LABELS[group.type] || group.type} — {group.name}</strong>
          {isOwner && (
            <button
              onClick={() => handleDeleteGroup(group.id)}
              style={{ padding: '4px 10px', cursor: 'pointer', fontSize: 12, background: 'transparent', border: '1px solid #fca5a5', color: '#dc3545', borderRadius: 8, fontWeight: 600 }}
            >
              Delete
            </button>
          )}
        </div>
        {group.plateNumber && <p style={{ margin: '4px 0', fontSize: 12, color: '#8a8f98' }}>Plate: {group.plateNumber}</p>}
        <p style={{ margin: '4px 0', fontSize: 12, color: '#8a8f98' }}>
          {acceptedMembers.length + 1}/{group.capacity + 1} {group.type === 'car' ? 'seats' : 'spots'} taken
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 4px', background: ACCENT_SOFT, borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
            {renderAvatar(group.owner, 20)}
            {group.owner?.firstName} {group.owner?.lastName}{isOwner ? ' (you)' : ' (owner)'}
          </span>
          {acceptedMembers.map((m) => (
            <span key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 4px', background: ACCENT_SOFT, borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
              {renderAvatar(m.customer, 20)}
              {m.customer?.firstName} {m.customer?.lastName}{m.customerId === customer?.id ? ' (you)' : ''}
            </span>
          ))}
          {isOwner && pendingMembers.map((m) => (
            <span key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 4px', background: '#fef9e7', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
              {renderAvatar(m.customer, 20)}
              {m.customer?.firstName} {m.customer?.lastName} (invited)
            </span>
          ))}
        </div>

        {isOwner && spotsLeft > 0 && (
          <button
            onClick={() => setInvitePickerGroupId(invitePickerGroupId === group.id ? null : group.id)}
            style={secondaryBtnStyle}
          >
            {invitePickerGroupId === group.id ? 'Close' : `Invite someone (${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left)`}
          </button>
        )}
        {isOwner && invitePickerGroupId === group.id && (
          <div style={{ marginTop: 10, maxHeight: 180, overflowY: 'auto', border: '1px solid #eef0f4', borderRadius: 10 }}>
            {attendees.filter((a) => a.id !== customer?.id && !alreadyInvitedIds.has(a.id)).length === 0 ? (
              <p style={{ margin: 0, padding: 10, fontSize: 12, color: '#8a8f98' }}>Everyone's already invited.</p>
            ) : (
              attendees
                .filter((a) => a.id !== customer?.id && !alreadyInvitedIds.has(a.id))
                .map((a) => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #f4f5f7', fontSize: 13 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {renderAvatar(a, 22)}
                      {a.firstName} {a.lastName}
                    </span>
                    <button onClick={() => handleInviteToGroup(group.id, a.id)} style={{ padding: '4px 12px', cursor: 'pointer', fontSize: 12, borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontWeight: 600 }}>Invite</button>
                  </div>
                ))
            )}
          </div>
        )}

        {!isOwner && myMembership?.status === 'invited' && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleRespondToInvite(group.id, 'accepted')}
              style={{ ...primaryBtnStyle(false), background: '#16a34a', boxShadow: '0 2px 8px rgba(22,163,74,0.25)', padding: '8px 16px', fontSize: 13 }}
            >
              Accept
            </button>
            <button
              onClick={() => handleRespondToInvite(group.id, 'declined')}
              style={{ ...secondaryBtnStyle, padding: '8px 16px', fontSize: 13 }}
            >
              Decline
            </button>
          </div>
        )}
        {!isOwner && myMembership?.status === 'accepted' && (
          <button
            onClick={() => handleLeaveGroup(group.id)}
            style={{ marginTop: 10, ...dangerBtnStyle, padding: '6px 14px' }}
          >
            Leave
          </button>
        )}
      </div>
    );
  };

  // Personal, single-use invite link — replaces the old always-reusable referral link for this
  // event specifically. One link per person invited: burned the moment they sign up, traced back
  // to whoever shared it, and only regenerated once it's been used.
  const renderInviteLinkCard = () => {
    const isUsed = !!myInviteLink?.usedByCustomerId;
    const isExpired = !isUsed && !!myInviteLink?.expiresAt && new Date(myInviteLink.expiresAt).getTime() < Date.now();
    const linkUrl = myInviteLink ? `${window.location.origin}/events/${eventSlug}?invite=${myInviteLink.code}` : null;
    return (
      <div style={cardStyle}>
        <strong>Invite someone</strong>
        <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#8a8f98' }}>
          This event is invite-only. This link works for one person, once — the moment they sign up with
          it, it's used, so only someone you send it to yourself can ever get in. It also expires 2 weeks
          after you generate it.
        </p>
        {!myInviteLink ? (
          <button onClick={handleGetInviteLink} disabled={inviteLinkBusy} style={primaryBtnStyle(inviteLinkBusy)}>
            {inviteLinkBusy ? 'Creating...' : 'Get my invite link'}
          </button>
        ) : isUsed ? (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              ✅ Used by {myInviteLink.usedBy?.firstName} {myInviteLink.usedBy?.lastName}
            </p>
            <button onClick={handleGetInviteLink} disabled={inviteLinkBusy} style={primaryBtnStyle(inviteLinkBusy)}>
              {inviteLinkBusy ? 'Creating...' : 'Get a new link'}
            </button>
          </>
        ) : isExpired ? (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#c05621', fontWeight: 600 }}>
              ⏳ This link expired unused.
            </p>
            <button onClick={handleGetInviteLink} disabled={inviteLinkBusy} style={primaryBtnStyle(inviteLinkBusy)}>
              {inviteLinkBusy ? 'Creating...' : 'Get a new link'}
            </button>
          </>
        ) : (
          <>
            <input
              readOnly
              value={linkUrl}
              onClick={(e) => e.target.select()}
              style={{ ...inputStyle, marginBottom: 4 }}
            />
            {myInviteLink.expiresAt && (
              <p style={{ margin: 0, fontSize: 12, color: '#8a8f98' }}>
                Expires {new Date(myInviteLink.expiresAt).toLocaleDateString()}
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Your opportunity to be a part of One Onda
        </p>
        <h1 style={{ margin: 0 }}>{event.name}</h1>
        {event.date && <p>{new Date(event.date).toLocaleString()}</p>}
        {event.location && <p style={{ margin: '0 0 4px' }}>{event.location}</p>}
        {event.digitalFileUrl && (
          <p style={{ margin: '0 0 12px' }}>
            <a href={event.digitalFileUrl} target="_blank" rel="noreferrer">📍 Site map / arrival instructions</a>
          </p>
        )}
      </div>

      {renderStatusStrip()}

      {error && <div style={{ color: 'crimson', margin: '12px 0' }}>{error}</div>}

      {!signupResult ? (
        <>
          {renderOverviewInfo()}

          {!isAuthenticated ? (
            <p>
              <Link to="/customer/login">Log in</Link> or <Link to="/customer/signup">create an account</Link> to sign up for this event.
            </p>
          ) : !isDeferred && !isCarTiers && !isImmediate ? (
            <p>Ticketing for this event isn't set up for online signup yet — please contact us.</p>
          ) : !clientSecret ? (
            <>
              {collectsCarPerson && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <label>
                    Cars<br />
                    <input type="number" min={0} value={carCount} onChange={(e) => setCarCount(Math.max(0, Number(e.target.value) || 0))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                    <span style={{ display: 'block', fontSize: 11, color: '#888' }}>0 if riding with someone</span>
                  </label>
                  <label>
                    People<br />
                    <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  </label>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>${carTierPricePreview?.toFixed(2)}</div>
                </div>
              )}
              {event.collectEmergencyContact && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
                  <input
                    placeholder="Emergency contact name"
                    value={emergencyContactName}
                    onChange={(e) => setEmergencyContactName(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <input
                    placeholder="Emergency contact phone"
                    value={emergencyContactPhone}
                    onChange={(e) => setEmergencyContactPhone(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Coming to the Saturday asado? Meat or veg?</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'meat'} onChange={() => setDietaryPreference('meat')} /> Meat
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'veg'} onChange={() => setDietaryPreference('veg')} /> Veg
                  </label>
                </div>
              </div>
              <button onClick={startCardSetup} style={{ padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
                {event.maxCapacity && progress && progress.confirmedCount >= event.maxCapacity
                  ? 'Join the waitlist'
                  : isImmediate
                    ? `Sign up & pay${carTierPricePreview != null ? ` $${carTierPricePreview.toFixed(2)}` : ''}`
                    : "Sign up — you won't be charged yet"}
              </button>
            </>
          ) : stripePromise && elementsOptions ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CardSetupForm
                onSaved={completeSignup}
                saving={savingCard}
                setSaving={setSavingCard}
                setError={setError}
                customer={customer}
                submitLabel={isImmediate ? `Pay${carTierPricePreview != null ? ` $${carTierPricePreview.toFixed(2)}` : ''} now` : undefined}
                savingLabel={isImmediate ? 'Charging your card...' : undefined}
              />
            </Elements>
          ) : (
            <p>Loading payment form...</p>
          )}
        </>
      ) : (
        <>
          {/* ---- Command Deck: icon tab bar + one content well ---- */}
          <div style={deckTabsWrap}>
            {TAB_DEFS.map((tab) => (
              <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={deckTabBtn(activeTab === tab.id)}>
                <div style={deckTabIcon}>{tab.ic}</div>
                <div style={deckTabLabel(activeTab === tab.id)}>{tab.label}</div>
                {TAB_BADGES[tab.id] && <div style={deckTabBadge}>{TAB_BADGES[tab.id]}</div>}
              </div>
            ))}
          </div>

          <div style={deckWell}>
            {activeTab === 'overview' && (
              <div>
                {renderOverviewInfo()}
                <div style={{ marginTop: 20 }}>{renderInviteLinkCard()}</div>
              </div>
            )}

            {activeTab === 'organisers' && (
              <div>
                <h3 style={sectionHeading}>Organisers</h3>
                <p style={sectionSub}>Pitch in and run a piece of the weekend.</p>
                {roles.length === 0 ? (
                  <p style={{ fontSize: 14, color: '#888' }}>No roles set up yet.</p>
                ) : (
                  roles.map((role) => (
                    <div key={role.id} style={cardStyle}>
                      <strong>{role.name}</strong>
                      {role.description && <p style={{ margin: '4px 0', color: '#666' }}>{role.description}</p>}
                      <p style={{ margin: '4px 0' }}>
                        Leader: {role.leader ? `${role.leader.firstName} ${role.leader.lastName}` : <em>unclaimed</em>}
                      </p>
                      {role.helpers?.length > 0 && (
                        <p style={{ margin: '4px 0' }}>
                          Helpers: {role.helpers.map((h) => `${h.customer.firstName} ${h.customer.lastName}`).join(', ')}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        {!role.leader && (
                          <button onClick={() => handleClaimLeader(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Become leader</button>
                        )}
                        <button onClick={() => handleJoinRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Join as helper</button>
                        <button onClick={() => handleLeaveRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Leave</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'addons' && (
              <div>
                <h3 style={sectionHeading}>Add-ons</h3>
                <p style={sectionSub}>Group-buys — chip in and split the cost.</p>
                {addOns.map((addOn) => {
                  const perShareLitres = addOn.minSharePerPerson;
                  const yieldUnit = (addOn.unitYieldLabel || '').replace(/^[\d.]+/, '').trim();
                  // pricePerShare = totalCost / totalShares — when it's under-subscribed, each
                  // current joiner is actually fronting more than their nominal share (e.g. the
                  // only person in is covering the whole keg, not just their 2L). Scale the
                  // displayed yield to match what that price is really paying for, not a fixed
                  // per-share amount that's only accurate once the unit is full.
                  const totalShares = addOn.pricing?.totalShares ?? 0;
                  const unitsNeeded = addOn.pricing?.unitsNeeded ?? 1;
                  const currentYield =
                    totalShares > 0
                      ? Math.round(((unitsNeeded * addOn.capacityPerUnit * perShareLitres) / totalShares) * 10) / 10
                      : perShareLitres;
                  return (
                    <div key={addOn.id} style={cardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <strong>{addOn.name}</strong>
                        {addOn.status === 'pending_approval' && <span style={{ fontSize: 12, color: '#f57c00' }}>(pending approval)</span>}
                      </div>
                      {addOn.description && (
                        <div style={{ margin: '4px 0' }}>
                          <button
                            type="button"
                            onClick={() => setExpandedAddOnId(expandedAddOnId === addOn.id ? null : addOn.id)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#666', textDecoration: 'underline' }}
                          >
                            {expandedAddOnId === addOn.id ? 'Hide details' : 'Details'}
                          </button>
                          {expandedAddOnId === addOn.id && (
                            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{addOn.description}</p>
                          )}
                        </div>
                      )}
                      <p style={{ margin: '4px 0', fontWeight: 600 }}>
                        ${addOn.pricing?.pricePerShare?.toFixed(2)} = {currentYield}{yieldUnit}
                        <span style={{ fontWeight: 400, color: '#888' }}> · fits ~{addOn.capacityPerUnit} people/unit · {addOn.pricing?.totalShares ?? 0} shares in ({addOn.pricing?.unitsNeeded ?? 1} unit{(addOn.pricing?.unitsNeeded ?? 1) > 1 ? 's' : ''})</span>
                      </p>
                      {totalShares > 0 && totalShares < unitsNeeded * addOn.capacityPerUnit && (
                        <p style={{ margin: '2px 0 4px', fontSize: 12, color: '#c05621' }}>
                          Under-subscribed right now — at {addOn.capacityPerUnit * unitsNeeded} people/unit full it'd be
                          ${(addOn.pricing?.totalCost / (addOn.capacityPerUnit * unitsNeeded)).toFixed(2)} = {perShareLitres}{yieldUnit} each.
                        </p>
                      )}
                      {addOn.participants?.length > 0 && (
                        <p style={{ margin: '4px 0', fontSize: 13, color: '#666' }}>
                          In: {addOn.participants.map((p) => `${p.name} (${p.quantity})`).join(', ')}
                        </p>
                      )}
                      {addOn.confirmDeadlineAt && !addOn.chargedAt && (() => {
                        const mine = addOn.participants?.find((p) => p.customerId === customer?.id);
                        if (!mine) return null;
                        return mine.confirmedForCharge ? (
                          <p style={{ margin: '6px 0', fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
                            ✅ You've confirmed — card will be charged by {new Date(addOn.confirmDeadlineAt).toLocaleDateString()}.
                          </p>
                        ) : (
                          <div style={{ margin: '6px 0', padding: 10, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}>
                            <p style={{ margin: '0 0 6px', fontSize: 13 }}>
                              We didn't quite fill this — confirm by {new Date(addOn.confirmDeadlineAt).toLocaleDateString()} at the price above, or you'll be released (never charged).
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => handleConfirmAddOnCharge(addOn.id)} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
                                Confirm
                              </button>
                              <button onClick={() => handleDeclineAddOn(addOn.id)} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 12, background: 'transparent', border: '1px solid #999', borderRadius: 4 }}>
                                No thanks
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {addOn.status === 'active' && !addOn.confirmDeadlineAt && (() => {
                        const mine = addOn.participants?.find((p) => p.customerId === customer?.id);
                        if (!mine) {
                          return (
                            <button onClick={() => handleAddOnSignup(addOn.id, 1)} style={{ padding: '8px 16px', cursor: 'pointer', marginTop: 6 }}>
                              Join in (1 share)
                            </button>
                          );
                        }
                        return (
                          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
                              ✅ You're in — {mine.quantity} share{mine.quantity === 1 ? '' : 's'}
                            </span>
                            {addOn.allowMultiplePerPerson && (
                              <button onClick={() => handleAddOnSignup(addOn.id, 1)} style={{ ...secondaryBtnStyle, padding: '5px 12px', fontSize: 12 }}>
                                Add another share
                              </button>
                            )}
                            <button onClick={() => handleDeclineAddOn(addOn.id)} style={dangerBtnStyle}>
                              Opt out
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}

                <div style={{ padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', marginTop: 14 }}>
                  <strong style={{ fontSize: 14 }}>Propose your own add-on</strong>
                  <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#666' }}>The organizer will need to approve it before others can join.</p>
                  <input
                    placeholder="Name (e.g. Coffee run)"
                    value={proposedAddOnForm.name}
                    onChange={(e) => setProposedAddOnForm((p) => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', padding: 8, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <input
                      placeholder="Unit cost ($)"
                      type="number"
                      value={proposedAddOnForm.unitCost}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitCost: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <input
                      placeholder="Unit yield (e.g. 10)"
                      type="number"
                      value={proposedAddOnForm.unitYield}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitYield: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <input
                      placeholder="Min share/person"
                      type="number"
                      value={proposedAddOnForm.minSharePerPerson}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, minSharePerPerson: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                  </div>
                  <button onClick={handleProposeAddOn} disabled={proposingAddOn} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                    {proposingAddOn ? 'Submitting...' : 'Propose add-on'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'gear' && (
              <div>
                <h3 style={sectionHeading}>Gear</h3>
                <p style={sectionSub}>For the communal area (Sites 6 & 7). Multiple people can offer the same thing — they all show up under it.</p>
                {renderNeedOfferBoard(gearItems, 'gear', 'Nothing needed yet.', 'I have this', 'Confirm offer')}
              </div>
            )}

            {activeTab === 'food' && (
              <div>
                <h3 style={sectionHeading}>Food & snacks</h3>
                <p style={sectionSub}>
                  Sharing meals is a big part of how we connect and communicate as a group. Your $60 covers food
                  for the weekend — here's the plan. Friday night we're looking for a few chefs to cook a couple
                  of dishes to share — put your hand up below if that's you. Saturday morning is fruit, eggs,
                  bread and coffee, then the asado starts at lunch and runs right through to dinner. Sunday's got
                  breakfast and dinner — if you want to cook for Sunday night too, call it below just like Friday.
                  Monday morning is a last breakfast before you head off. If we ever run short, Zee's got pancake
                  ingredients as backup.
                </p>
                {renderWishlistBoard(foodItems, 'snacks', 'Nothing on the list yet — start it off below.')}

                {['friday', 'sunday'].map((occasion) => {
                  const occasionDishes = dishes.filter((d) => d.occasion === occasion);
                  return (
                    <div key={occasion} style={{ marginTop: 22 }}>
                      <h3 style={sectionHeading}>{occasion === 'friday' ? 'Friday night' : 'Sunday night'}</h3>
                      <p style={sectionSub}>
                        {occasion === 'friday'
                          ? "Keen to cook Friday? Put your name down and roughly what you're thinking — nothing's locked in yet. Once we know how many people are coming, we'll look at everyone's ideas and settle on a final plan so we're not all making the same thing."
                          : 'Same for Sunday — say if you\'re keen and what you\'re thinking of making.'}
                      </p>
                      {occasionDishes.length === 0 ? (
                        <p style={{ fontSize: 14, color: '#888' }}>Nobody's put their hand up for {occasion === 'friday' ? 'Friday' : 'Sunday'} yet — be the first.</p>
                      ) : (
                        occasionDishes.map(renderDishCard)
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <input
                          placeholder="What are you thinking of making? (e.g. chicken curry)"
                          value={newDishOccasion === occasion ? newFridayDishName : ''}
                          onFocus={() => setNewDishOccasion(occasion)}
                          onChange={(e) => {
                            setNewDishOccasion(occasion);
                            setNewFridayDishName(e.target.value);
                          }}
                          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                        <button
                          onClick={() => handleAddDish(newFridayDishName, occasion, () => setNewFridayDishName(''))}
                          disabled={addingDish || newDishOccasion !== occasion || !newFridayDishName.trim()}
                          style={{ padding: '10px 16px', cursor: 'pointer' }}
                        >
                          {addingDish && newDishOccasion === occasion ? 'Adding...' : "I'm keen"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h3 style={sectionHeading}>Gallery</h3>
                <p style={sectionSub}>Share photos and videos from the event — anyone can grab a copy for themselves.</p>

                <div style={{ ...cardStyle, marginBottom: 14 }}>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setGalleryUploadFile(e.target.files?.[0] || null)}
                    style={{ fontSize: 13 }}
                  />
                  {galleryUploadFile && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        placeholder="Caption (optional)"
                        value={galleryCaption}
                        onChange={(e) => setGalleryCaption(e.target.value)}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginBottom: 8 }}
                      />
                      <button onClick={handleUploadGalleryPhoto} disabled={galleryUploading} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                        {galleryUploading ? 'Sharing...' : 'Share to gallery'}
                      </button>
                    </div>
                  )}
                </div>

                {galleryPhotos.length === 0 ? (
                  <p style={{ fontSize: 14, color: '#888' }}>Nothing shared yet — be the first!</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                    {galleryPhotos.map((photo) => {
                      const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(photo.photoUrl);
                      const isMine = photo.uploadedByCustomer?.id === customer?.id;
                      return (
                        <div key={photo.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                          {isVideo ? (
                            <video src={photo.photoUrl} controls style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <img src={photo.photoUrl} alt={photo.caption || ''} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                          )}
                          <div style={{ padding: 6 }}>
                            {photo.caption && <p style={{ margin: 0, fontSize: 12 }}>{photo.caption}</p>}
                            <p style={{ margin: '2px 0 4px', fontSize: 11, color: '#888' }}>{photo.uploadedByCustomer?.firstName}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <a href={photo.photoUrl} download target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>Download</a>
                              {isMine && (
                                <button
                                  onClick={() => handleDeleteGalleryPhoto(photo.id)}
                                  style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 11, padding: 0 }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'mystuff' && (
              <div>
                <h3 style={sectionHeading}>My stuff</h3>
                <p style={sectionSub}>Manage your spot, your crew, and everything you've put your hand up for.</p>

                {signupResult.waitlisted ? (
                  <div style={{ ...cardStyle, background: '#fffbeb', borderColor: '#fde68a' }}>
                    <strong>You're on the waitlist!</strong>
                    <p style={{ margin: '4px 0 0' }}>We'll charge your card and confirm your spot if the event expands.</p>
                  </div>
                ) : signupResult.data?.status === 'payment_failed' ? (
                  <div style={{ ...cardStyle, background: '#fef2f2', borderColor: '#fca5a5' }}>
                    <strong>Your payment didn't go through</strong>
                    <p style={{ margin: '4px 0 8px' }}>The guest list is locked in, but we couldn't charge the card on file. Update your card to secure your spot.</p>
                    {!retryClientSecret ? (
                      <button onClick={startPaymentRetry} style={primaryBtnStyle(false)}>Update card</button>
                    ) : stripePromise ? (
                      <Elements stripe={stripePromise} options={{ clientSecret: retryClientSecret }}>
                        <CardSetupForm onSaved={completeRetry} saving={retrySaving} setSaving={setRetrySaving} setError={setError} customer={customer} />
                      </Elements>
                    ) : (
                      <p>Loading payment form...</p>
                    )}
                  </div>
                ) : signupResult.data?.status === 'valid' ? (
                  <div style={{ ...cardStyle, background: '#f0fdf4', borderColor: '#86efac' }}>
                    <strong>Confirmed and charged — see you there!</strong>
                  </div>
                ) : (
                  <div style={{ ...cardStyle, background: '#f0fdf4', borderColor: '#86efac' }}>
                    <strong>Confirmed to attend</strong>
                    <p style={{ margin: '4px 0 8px' }}>
                      {isImmediate ? 'Your card is being charged — refresh in a moment if this sticks around.' : 'Card saved, not charged until we hit the signup threshold.'}
                    </p>
                    <button onClick={handleCancelSignup} disabled={cancelling} style={dangerBtnStyle}>
                      {cancelling ? 'Cancelling...' : 'Cancel my spot'}
                    </button>
                  </div>
                )}

                <div style={cardStyle}>
                  <strong>Meat or veg</strong>
                  <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#8a8f98' }}>For the Saturday asado — change it any time before then.</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['meat', 'veg'].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleUpdateDietary(opt)}
                        disabled={updatingDietary}
                        style={{ ...pillBtnStyle(signupResult.data?.dietaryPreference === opt), textTransform: 'capitalize' }}
                      >
                        {opt}
                      </button>
                    ))}
                    {signupResult.data?.dietaryPreference && (
                      <button
                        onClick={() => handleUpdateDietary(null)}
                        disabled={updatingDietary}
                        style={{ ...pillBtnStyle(false), color: '#999' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Cars & sleeping spaces</strong>
                    <button
                      onClick={() => setShowGroupForm(!showGroupForm)}
                      style={showGroupForm ? { ...secondaryBtnStyle, padding: '6px 14px', fontSize: 12 } : { ...primaryBtnStyle(false), padding: '6px 14px', fontSize: 12 }}
                    >
                      {showGroupForm ? 'Close' : '+ New'}
                    </button>
                  </div>
                  <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#8a8f98' }}>
                    Got a car with spare seats, or a tent/van with room? Create it and invite specific people —
                    only whoever's in it can see who's in it.
                  </p>

                  {showGroupForm && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        {Object.entries(GROUP_TYPE_LABELS).map(([val, label]) => (
                          <button
                            key={val}
                            onClick={() => setNewGroupForm({ ...newGroupForm, type: val })}
                            style={pillBtnStyle(newGroupForm.type === val)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <input
                        placeholder="Name it (e.g. Zee's car, Blue tent)"
                        value={newGroupForm.name}
                        onChange={(e) => setNewGroupForm({ ...newGroupForm, name: e.target.value })}
                        style={inputStyle}
                      />
                      {newGroupForm.type === 'car' && (
                        <input
                          placeholder="Number plate (optional)"
                          value={newGroupForm.plateNumber}
                          onChange={(e) => setNewGroupForm({ ...newGroupForm, plateNumber: e.target.value })}
                          style={inputStyle}
                        />
                      )}
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 12, color: '#555' }}>
                        Spare {newGroupForm.type === 'car' ? 'seats' : 'spots'} (not counting you)
                        <input
                          type="number"
                          min={0}
                          value={newGroupForm.capacity}
                          onChange={(e) => setNewGroupForm({ ...newGroupForm, capacity: Math.max(0, Number(e.target.value) || 0) })}
                          style={{ width: 70, marginLeft: 8, padding: 7, borderRadius: 8, border: '1px solid #dcdfe6' }}
                        />
                      </label>
                      <button
                        onClick={async () => { await handleCreateGroup(); setShowGroupForm(false); }}
                        disabled={groupBusy || !newGroupForm.name.trim()}
                        style={primaryBtnStyle(groupBusy || !newGroupForm.name.trim())}
                      >
                        {groupBusy ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  )}
                </div>

                {myCarGroups.length > 0 && (
                  <>
                    <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#8a8f98', margin: '18px 0 6px' }}>
                      My car{myCarGroups.length > 1 ? 's' : ''}
                    </p>
                    {myCarGroups.map(renderGroupCard)}
                  </>
                )}
                {mySleepGroups.length > 0 && (
                  <>
                    <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#8a8f98', margin: '18px 0 6px' }}>
                      My sleeping space{mySleepGroups.length > 1 ? 's' : ''}
                    </p>
                    {mySleepGroups.map(renderGroupCard)}
                  </>
                )}

                <div style={cardStyle}>
                  <strong>What I've offered</strong>
                  {myOfferedItems.length === 0 ? (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8a8f98' }}>Nothing yet — check the Gear or Food tabs.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                      {myOfferedItems.map((it) => (
                        <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#555' }}>
                          <span>{it.name}</span>
                          <button onClick={() => handleWithdrawOffer(it.id)} style={{ ...secondaryBtnStyle, padding: '4px 10px', fontSize: 12 }}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={cardStyle}>
                  <strong>Add-ons I've joined</strong>
                  {myAddOns.length === 0 ? (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8a8f98' }}>Nothing yet — check the Add-ons tab.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                      {myAddOns.map((a) => (
                        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#555' }}>
                          <span>{a.name}</span>
                          {a.chargedAt ? (
                            <span style={{ fontSize: 11, color: '#8a8f98' }}>Already charged</span>
                          ) : (
                            <button onClick={() => handleDeclineAddOn(a.id)} style={{ ...secondaryBtnStyle, padding: '4px 10px', fontSize: 12 }}>
                              Opt out
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {renderInviteLinkCard()}
              </div>
            )}

            {activeTab === 'chat' && (
              <div>
                <h3 style={sectionHeading}>Group chat</h3>
                <p style={sectionSub}>One shared space — filter by topic below, or view everything under "All".</p>

                <div style={{ ...cardStyle, marginBottom: 14, cursor: 'pointer' }} onClick={() => setPeopleExpanded(!peopleExpanded)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>🧑‍🤝‍🧑 People{unreadMessagesCount > 0 ? ` (${unreadMessagesCount})` : ''}</strong>
                    <span style={{ fontSize: 12, color: '#666' }}>{peopleExpanded ? 'Hide ▲' : 'Show ▼'}</span>
                  </div>
                </div>

                {peopleExpanded && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={sectionSub}>
                      Everyone confirmed for the weekend. Message anyone, or invite them into your car or sleeping space from My Stuff.
                    </p>

                    <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
                      {renderAvatar(customer, 44)}
                      <div>
                        <strong>Your photo</strong>
                        <p style={{ margin: '2px 0 6px', fontSize: 12, color: '#666' }}>
                          Add a photo so people recognise you in the directory and chat.
                        </p>
                        <label style={{ display: 'inline-block', padding: '5px 12px', fontSize: 12, border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>
                          {avatarUploading ? 'Uploading...' : customer?.avatarUrl ? 'Change photo' : 'Add photo'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => e.target.files?.[0] && handleUploadAvatar(e.target.files[0])}
                            disabled={avatarUploading}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    </div>

                {messageThreads.length > 0 && (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>Messages</p>
                    {messageThreads.map((t) => (
                      <div
                        key={t.customer.id}
                        onClick={() => handleOpenThread(t.customer.id)}
                        style={{ ...cardStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {renderAvatar(t.customer, 30)}
                          <div>
                            <strong>{t.customer.firstName} {t.customer.lastName}</strong>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>{t.lastMessage}</p>
                          </div>
                        </div>
                        {t.unreadCount > 0 && (
                          <span style={{ background: '#dc3545', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: 11 }}>
                            {t.unreadCount}
                          </span>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {activeThreadCustomerId && (() => {
                  const threadPerson = attendees.find((a) => a.id === activeThreadCustomerId) ||
                    messageThreads.find((t) => t.customer.id === activeThreadCustomerId)?.customer ||
                    {};
                  return (
                  <div style={{ ...cardStyle, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {renderAvatar(threadPerson, 26)}
                        {threadPerson.firstName} {threadPerson.lastName}
                      </strong>
                      <button onClick={() => setActiveThreadCustomerId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                      {threadMessages.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: '#888' }}>No messages yet — say hi.</p>
                      ) : (
                        threadMessages.map((m) => (
                          <div
                            key={m.id}
                            style={{
                              alignSelf: m.fromCustomerId === customer?.id ? 'flex-end' : 'flex-start',
                              background: m.fromCustomerId === customer?.id ? '#333' : '#f0f0f0',
                              color: m.fromCustomerId === customer?.id ? '#fff' : '#333',
                              padding: '6px 12px', borderRadius: 14, fontSize: 13, maxWidth: '80%',
                            }}
                          >
                            {m.content}
                          </div>
                        ))
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                        placeholder="Message..."
                        style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                      />
                      <button onClick={handleSendMessage} disabled={sendingMessage || !messageInput.trim()} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                        {sendingMessage ? '...' : 'Send'}
                      </button>
                    </div>
                  </div>
                  );
                })()}

                    <p style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 4px' }}>Everyone attending</p>
                    {attendees.filter((a) => a.id !== customer?.id).length === 0 ? (
                      <p style={{ fontSize: 14, color: '#888' }}>Nobody else confirmed yet.</p>
                    ) : (
                      attendees
                        .filter((a) => a.id !== customer?.id)
                        .map((a) => (
                          <div key={a.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {renderAvatar(a, 28)}
                              {a.firstName} {a.lastName}
                            </span>
                            <button onClick={() => handleOpenThread(a.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>Message</button>
                          </div>
                        ))
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <button
                    onClick={() => setSelectedTopicId(null)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                      background: selectedTopicId === null ? '#333' : '#fff',
                      color: selectedTopicId === null ? '#fff' : '#333',
                    }}
                  >
                    All
                  </button>
                  {chatTopics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopicId(topic.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                        background: selectedTopicId === topic.id ? '#333' : '#fff',
                        color: selectedTopicId === topic.id ? '#fff' : '#333',
                      }}
                    >
                      {topic.name}
                    </button>
                  ))}
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, padding: 10, marginBottom: 10, background: '#fff' }}>
                  {chatLoadError ? (
                    <p style={{ fontSize: 14, color: 'crimson' }}>Couldn't load chat: {chatLoadError}</p>
                  ) : chatMessages.length === 0 ? (
                    <p style={{ fontSize: 14, color: '#888' }}>No messages yet — say hi!</p>
                  ) : (
                    chatMessages.map((msg) => (
                      <div key={msg.id} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 13 }}>
                          <strong>{msg.isAdmin ? 'Organizer' : msg.isStaff ? 'Staff' : `${msg.customer?.firstName || ''} ${msg.customer?.lastName || ''}`}</strong>
                          {msg.topic?.name && <span style={{ color: '#888', marginLeft: 6 }}>#{msg.topic.name}</span>}
                          {msg.status === 'pending' && <span style={{ color: '#f57c00', marginLeft: 6 }}>(pending approval)</span>}
                        </div>
                        <p style={{ margin: '2px 0' }}>{msg.content}</p>
                        {msg.photoUrl && <img src={msg.photoUrl} alt="attachment" style={{ maxWidth: 160, borderRadius: 6 }} />}
                      </div>
                    ))
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={showItemOffer} onChange={(e) => setShowItemOffer(e.target.checked)} />
                  Offering gear/an item with this message
                </label>
                {showItemOffer && (
                  <input
                    placeholder="Item name (e.g. Generator, big pot)"
                    value={chatItemName}
                    onChange={(e) => setChatItemName(e.target.value)}
                    style={{ width: '100%', padding: 10, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    placeholder={selectedTopicId ? `Message in #${chatTopics.find((t) => t.id === selectedTopicId)?.name || ''}...` : 'Message...'}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    style={{ flex: 1, minWidth: 140, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  {showItemOffer && (
                    <input type="file" accept="image/*" onChange={(e) => setChatPhotoFile(e.target.files?.[0] || null)} />
                  )}
                  <button onClick={handleSendChatMessage} disabled={sendingChat} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                    {sendingChat ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
